// ============================================
// BUSCADORINTELIGENTE.JS — el buscador de La Tienda para cualquier lista de productos
// ============================================
// BUSCADOR-UNICO (2026-10-12). Listas pactadas, Colecciones y Mis Productos no tenian buscador (o
// solo un "contiene" a mano). Este componente NO busca por su cuenta: manda lo que la pantalla ya
// tiene cargado a POST /api/buscador/filtrar, que usa el MISMO motor de La Tienda (palabras
// completas, codigos con o sin guiones, acentos, sinonimos de la tabla, orden por modelo/familia/
// marca/titulo y, si no sale nada exacto, tolerancia a erratas). Asi un solo motor sirve a las
// cuatro pantallas y no hay dos buscadores que se desincronicen.
//
// Uso:
//   const busq = useBuscadorInteligente(items, (it) => ({ id: it.id, titulo: it.nombre, marca, modelo }));
//   <BuscadorInteligente busq={busq} placeholder="Buscar en esta lista…" />
//   {busq.visibles.map(...)}            // filtrados y ordenados por relevancia
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { fetchAuth } from '../utils/sesion';
import { API_BASE } from '../config/api';
import { tokens } from '../styles/tokens';

const ESPERA_MS = 250;

export function useBuscadorInteligente(items, aCandidato) {
  const [q, setQ] = useState('');
  const [res, setRes] = useState(null); // { q, ids, aproximado, sugerencia } | { q, error }
  const [buscando, setBuscando] = useState(false);
  // La funcion la pasa cada pantalla en linea: se guarda en una ref para que no dispare busquedas.
  const aCandRef = useRef(aCandidato);
  aCandRef.current = aCandidato;
  const lista = useMemo(() => (Array.isArray(items) ? items : []), [items]);

  useEffect(() => {
    const t = q.trim();
    if (!t) { setRes(null); setBuscando(false); return undefined; }
    let vivo = true;
    setBuscando(true);
    const h = setTimeout(async () => {
      try {
        const candidatos = lista.map((it) => {
          const c = aCandRef.current(it) || {};
          return { id: String(c.id), titulo: c.titulo || '', marca: c.marca || '', modelo: c.modelo || '' };
        });
        const r = await fetchAuth(`${API_BASE}/api/buscador/filtrar`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ q: t, candidatos }),
        });
        const d = await r.json().catch(() => ({}));
        if (!vivo) return;
        setRes(r.ok && d.ok ? { q: t, ids: d.ids || [], aproximado: !!d.aproximado, sugerencia: d.sugerencia || null } : { q: t, error: true });
      } catch (e) {
        if (vivo && e.code !== 'SESION_EXPIRADA') setRes({ q: t, error: true });
      } finally {
        if (vivo) setBuscando(false);
      }
    }, ESPERA_MS);
    return () => { vivo = false; clearTimeout(h); };
  }, [q, lista]);

  const visibles = useMemo(() => {
    const t = q.trim();
    if (!t || !res || res.q !== t) return lista;
    if (res.error) {
      // Sin servidor no se deja la lista vacia: "contiene" simple, sin acentos, como red.
      const n = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
      const tn = n(t);
      return lista.filter((it) => { const c = aCandRef.current(it) || {}; return n(`${c.titulo} ${c.marca} ${c.modelo}`).includes(tn); });
    }
    const pos = new Map(res.ids.map((id, i) => [id, i]));
    return lista
      .filter((it) => pos.has(String((aCandRef.current(it) || {}).id)))
      .sort((a, b) => pos.get(String(aCandRef.current(a).id)) - pos.get(String(aCandRef.current(b).id)));
  }, [q, res, lista]);

  const activo = q.trim() !== '';
  return {
    q, setQ, visibles, buscando, activo,
    total: lista.length,
    aproximado: !!(res && res.aproximado && res.q === q.trim()),
    sugerencia: res && res.q === q.trim() ? res.sugerencia : null,
  };
}

export default function BuscadorInteligente({ busq, placeholder = 'Buscar…', style }) {
  const { q, setQ, visibles, buscando, activo, total, aproximado, sugerencia } = busq;
  return (
    <div style={{ marginBottom: 12, ...style }}>
      <div style={{ position: 'relative' }}>
        <Search size={16} color={tokens.textSecondary} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
        <input
          type="text" inputMode="search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={placeholder} aria-label={placeholder} enterKeyHint="search" autoComplete="off"
          style={{
            width: '100%', boxSizing: 'border-box', minHeight: 44, padding: '10px 40px 10px 36px',
            border: `1px solid ${tokens.border}`, borderRadius: 10, fontSize: 16,
            fontFamily: 'inherit', background: tokens.surface, color: tokens.text, outline: 'none',
          }}
        />
        {activo && (
          <button
            type="button" onClick={() => setQ('')} aria-label="Limpiar búsqueda"
            style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', width: 36, height: 36, border: 'none', background: 'transparent', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          ><X size={16} color={tokens.textSecondary} /></button>
        )}
      </div>
      {activo && (
        <div style={{ fontSize: 12, color: tokens.textSecondary, marginTop: 6, lineHeight: 1.5 }} aria-live="polite">
          {buscando ? 'Buscando…' : visibles.length > 0 ? (
            <span>
              {visibles.length} de {total}
              {aproximado && <> · no hay nada escrito asi, se muestran los <strong>parecidos</strong></>}
            </span>
          ) : sugerencia ? (
            <span>
              Nada con "{q.trim()}". ¿Quisiste decir{' '}
              <button type="button" onClick={() => setQ(sugerencia.texto)} style={{ border: 'none', background: 'none', padding: 0, color: tokens.primary, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', fontSize: 12, fontFamily: 'inherit' }}>
                {sugerencia.texto}
              </button>?
            </span>
          ) : <span>Nada con "{q.trim()}" en esta lista.</span>}
        </div>
      )}
    </div>
  );
}
